/**
 * CrazyGames SDK Integration
 * 
 * Provides a safe wrapper around the CrazyGames SDK with stubs
 * for local development and testing.
 * 
 * @see https://docs.crazygames.com/sdk/html5/
 */

// CrazyGames SDK types
interface CrazyGamesSDKType {
  game: {
    gameplayStart: () => void;
    gameplayStop: () => void;
    happytime: () => void;
    sdkGameLoadingStart: () => void;
    sdkGameLoadingStop: () => void;
  };
  ad: {
    requestAd: (type: 'midgame' | 'rewarded', callbacks: {
      adStarted?: () => void;
      adFinished?: () => void;
      adError?: (error: string) => void;
    }) => void;
    hasAdblock: boolean;
  };
  user: {
    isUserAccountAvailable: boolean;
  };
  data: {
    getItem: (key: string) => Promise<string | null>;
    setItem: (key: string, value: string) => Promise<void>;
    removeItem: (key: string) => Promise<void>;
    clear: () => Promise<void>;
  };
}

declare global {
  interface Window {
    CrazyGames?: {
      SDK?: CrazyGamesSDKType;
    };
  }
}

/**
 * CrazyGames SDK Wrapper
 * 
 * Provides safe access to CrazyGames SDK features with fallback stubs.
 * All methods are safe to call even if SDK is not loaded.
 */
export class CrazyGamesSDK {
  private static instance: CrazyGamesSDK;
  private sdk: CrazyGamesSDKType | null = null;
  private isInitialized = false;
  
  private constructor() {}
  
  /**
   * Get singleton instance
   */
  static getInstance(): CrazyGamesSDK {
    if (!CrazyGamesSDK.instance) {
      CrazyGamesSDK.instance = new CrazyGamesSDK();
    }
    return CrazyGamesSDK.instance;
  }
  
  /**
   * Initialize SDK connection
   * Safe to call multiple times
   */
  async init(): Promise<boolean> {
    if (this.isInitialized) {
      return this.sdk !== null;
    }
    
    this.isInitialized = true;
    
    try {
      // Check if SDK is available
      if (window.CrazyGames?.SDK) {
        this.sdk = window.CrazyGames.SDK;
        console.log('[CrazyGamesSDK] SDK initialized successfully');
        return true;
      }
      
      console.log('[CrazyGamesSDK] SDK not available, using stubs');
      return false;
    } catch (error) {
      console.warn('[CrazyGamesSDK] Failed to initialize:', error);
      return false;
    }
  }
  
  /**
   * Check if SDK is available
   */
  isAvailable(): boolean {
    return this.sdk !== null;
  }
  
  /**
   * Notify SDK that gameplay has started
   * Call when player starts actual gameplay (not menus)
   */
  gameplayStart(): void {
    if (this.sdk) {
      try {
        this.sdk.game.gameplayStart();
        console.log('[CrazyGamesSDK] Gameplay started');
      } catch (error) {
        console.warn('[CrazyGamesSDK] gameplayStart failed:', error);
      }
    }
  }
  
  /**
   * Notify SDK that gameplay has stopped
   * Call when player dies, pauses, or returns to menu
   */
  gameplayStop(): void {
    if (this.sdk) {
      try {
        this.sdk.game.gameplayStop();
        console.log('[CrazyGamesSDK] Gameplay stopped');
      } catch (error) {
        console.warn('[CrazyGamesSDK] gameplayStop failed:', error);
      }
    }
  }
  
  /**
   * Trigger happy time (player achievement moment)
   * Call when player achieves something notable
   */
  happytime(): void {
    if (this.sdk) {
      try {
        this.sdk.game.happytime();
        console.log('[CrazyGamesSDK] Happytime triggered');
      } catch (error) {
        console.warn('[CrazyGamesSDK] happytime failed:', error);
      }
    }
  }
  
  /**
   * Notify SDK that game loading has started
   */
  loadingStart(): void {
    if (this.sdk) {
      try {
        this.sdk.game.sdkGameLoadingStart();
        console.log('[CrazyGamesSDK] Loading started');
      } catch (error) {
        console.warn('[CrazyGamesSDK] loadingStart failed:', error);
      }
    }
  }
  
  /**
   * Notify SDK that game loading has finished
   */
  loadingStop(): void {
    if (this.sdk) {
      try {
        this.sdk.game.sdkGameLoadingStop();
        console.log('[CrazyGamesSDK] Loading stopped');
      } catch (error) {
        console.warn('[CrazyGamesSDK] loadingStop failed:', error);
      }
    }
  }
  
  /**
   * Request an advertisement
   * @param type - 'midgame' for break ads, 'rewarded' for optional reward ads
   * @param onComplete - Callback when ad finishes or fails
   */
  requestAd(
    type: 'midgame' | 'rewarded',
    onComplete: (success: boolean, rewarded?: boolean) => void
  ): void {
    if (!this.sdk) {
      // No SDK, simulate ad completion
      console.log(`[CrazyGamesSDK] Ad request (${type}) - no SDK, simulating completion`);
      setTimeout(() => onComplete(true, type === 'rewarded'), 500);
      return;
    }
    
    try {
      this.sdk.ad.requestAd(type, {
        adStarted: () => {
          console.log(`[CrazyGamesSDK] Ad started (${type})`);
        },
        adFinished: () => {
          console.log(`[CrazyGamesSDK] Ad finished (${type})`);
          onComplete(true, type === 'rewarded');
        },
        adError: (error) => {
          console.warn(`[CrazyGamesSDK] Ad error (${type}):`, error);
          onComplete(false, false);
        }
      });
    } catch (error) {
      console.warn('[CrazyGamesSDK] requestAd failed:', error);
      onComplete(false, false);
    }
  }
  
  /**
   * Check if user has adblock enabled
   */
  hasAdblock(): boolean {
    return this.sdk?.ad?.hasAdblock ?? false;
  }
  
  /**
   * Check if Data Module is available
   */
  isDataModuleAvailable(): boolean {
    return this.sdk?.data !== undefined;
  }
  
  /**
   * Get item from CrazyGames cloud storage
   * Falls back to localStorage if SDK not available
   * @param key - Storage key
   * @returns The stored value or null
   */
  async getItem(key: string): Promise<string | null> {
    if (this.sdk?.data) {
      try {
        const value = await this.sdk.data.getItem(key);
        console.log(`[CrazyGamesSDK] Data.getItem(${key}):`, value);
        return value;
      } catch (error) {
        console.warn(`[CrazyGamesSDK] Data.getItem(${key}) failed:`, error);
      }
    }
    
    // Fallback to localStorage
    return localStorage.getItem(key);
  }
  
  /**
   * Set item in CrazyGames cloud storage
   * Falls back to localStorage if SDK not available
   * @param key - Storage key
   * @param value - Value to store
   */
  async setItem(key: string, value: string): Promise<void> {
    if (this.sdk?.data) {
      try {
        await this.sdk.data.setItem(key, value);
        console.log(`[CrazyGamesSDK] Data.setItem(${key}):`, value);
        return;
      } catch (error) {
        console.warn(`[CrazyGamesSDK] Data.setItem(${key}) failed:`, error);
      }
    }
    
    // Fallback to localStorage
    localStorage.setItem(key, value);
  }
  
  /**
   * Remove item from CrazyGames cloud storage
   * Falls back to localStorage if SDK not available
   * @param key - Storage key
   */
  async removeItem(key: string): Promise<void> {
    if (this.sdk?.data) {
      try {
        await this.sdk.data.removeItem(key);
        console.log(`[CrazyGamesSDK] Data.removeItem(${key})`);
        return;
      } catch (error) {
        console.warn(`[CrazyGamesSDK] Data.removeItem(${key}) failed:`, error);
      }
    }
    
    // Fallback to localStorage
    localStorage.removeItem(key);
  }
}

export default CrazyGamesSDK;
